import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Upsell: same contract as product-bundle-list-buy-buttons — listen on product-form-component,
 * update rows from variant:update detail.resource, cart uses #byProduct + JSON fallback.
 */
class ProductUpsellBuyButtons extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** productId -> { variantId: string; price: number } */
  #byProduct = new Map();

  /** @type {{ moneyFormat: string; currency: string } | null} */
  #ui = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const cfgEl = this.querySelector('script[type="application/json"][data-upsell-ui-config]');
    try {
      this.#ui = cfgEl?.textContent ? JSON.parse(cfgEl.textContent) : null;
    } catch {
      this.#ui = null;
    }

    const form = this.closest('product-form-component');
    form?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.querySelectorAll('[data-upsell-checkbox]').forEach((el) => {
      el.addEventListener('click', (ev) => ev.stopPropagation(), { signal });
      el.addEventListener('change', () => this.#syncAvailability(), { signal });
    });

    this.querySelectorAll('[data-upsell-card]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const pid = String(node.dataset.productId ?? '');
      const vid = String(node.dataset.defaultVariantId ?? '');
      if (pid && vid) {
        this.#byProduct.set(pid, { variantId: vid, price: Number(node.dataset.defaultPrice) || 0 });
      }
    });
    this.#syncAvailability();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {HTMLElement} card */
  #variantJson(card) {
    const script = card
      .querySelector('[data-upsell-picker-wrap] variant-picker')
      ?.querySelector('script[type="application/json"]');
    if (!script?.textContent) return null;
    try {
      const j = JSON.parse(script.textContent);
      return j && typeof j === 'object' ? j : null;
    } catch {
      return null;
    }
  }

  /**
   * @returns {Array<{ variantId: string; quantity: number }>}
   */
  getUpsellLineItems() {
    /** @type {Array<{ variantId: string; quantity: number }>} */
    const out = [];
    this.querySelectorAll('[data-upsell-card]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const cb = node.querySelector('[data-upsell-checkbox]');
      if (!(cb instanceof HTMLInputElement) || !cb.checked) return;
      const pid = String(node.dataset.productId ?? '');
      if (!pid) return;

      let variantId = this.#byProduct.get(pid)?.variantId || '';
      if (!variantId || !/^\d+$/.test(variantId)) {
        const j = this.#variantJson(node);
        if (j?.id != null) variantId = String(j.id);
      }
      if (!variantId || !/^\d+$/.test(variantId)) {
        variantId = String(node.dataset.defaultVariantId ?? '');
      }
      if (variantId && /^\d+$/.test(variantId)) out.push({ variantId, quantity: 1 });
    });
    return out;
  }

  /** @param {Event} e */
  #onVariantUpdate = (e) => {
    const detail = /** @type {{ resource?: any; data?: { productId?: string } } | undefined} */ (
      /** @type {any} */ (e).detail
    );
    if (!detail) return;
    const pid = String(detail.data?.productId ?? '');
    if (!pid || !this.#byProduct.has(pid)) return;
    const resource = detail.resource;
    if (!resource?.id) return;
    this.#byProduct.set(pid, {
      variantId: String(resource.id),
      price: Number(resource.price) || 0,
    });
    this.#updateCardRow(pid, resource);
    this.#syncAvailability();
  };

  /**
   * @param {string} productId
   * @param {*} resource
   */
  #updateCardRow(productId, resource) {
    const card = this.querySelector(`[data-upsell-card][data-product-id="${productId}"]`);
    if (!(card instanceof HTMLElement)) return;

    const picker = card.querySelector('[data-upsell-picker-wrap] variant-picker');
    if (picker) {
      const vals = Array.isArray(resource.options)
        ? resource.options
        : [resource.option1, resource.option2, resource.option3].filter((x) => x != null && x !== '');
      picker.querySelectorAll('fieldset[data-fieldset-index]').forEach((fs) => {
        if (!(fs instanceof HTMLFieldSetElement)) return;
        const fi = parseInt(fs.dataset.fieldsetIndex ?? '-1', 10);
        if (fi < 0 || fi > 2) return;
        const val = vals[fi];
        if (val == null || val === '') return;
        const sw = fs.querySelector('legend .variant-option__swatch-value');
        if (sw) sw.textContent = String(val);
      });
    }

    const optsEl = card.querySelector('[data-upsell-line-opts]');
    if (optsEl) {
      const optionNames = (card.dataset.optionNames || '')
        .split('||')
        .map((s) => s.trim())
        .filter(Boolean);
      const optionValues = Array.isArray(resource.options)
        ? resource.options
        : [resource.option1, resource.option2, resource.option3].filter((v) => v != null && v !== '');
      if (optionNames.length > 0 && optionValues.length > 0) {
        optsEl.innerHTML = optionValues
          .map((/** @type {unknown} */ value, /** @type {number} */ i) => {
            const name = optionNames[i] || `Option ${i + 1}`;
            return `<span class="product-main__bundle-line-opt">${this.#escape(String(name))}: ${this.#escape(String(value))}</span>`;
          })
          .join('');
      } else if (resource.title) {
        optsEl.innerHTML = `<span class="product-main__bundle-line-opt">${this.#escape(String(resource.title))}</span>`;
      }
    }

    const fmt = this.#ui?.moneyFormat ?? '${{amount}}';
    const cur = this.#ui?.currency ?? '';
    const priceEl = card.querySelector('[data-upsell-line-price]');
    const cmpEl = card.querySelector('[data-upsell-line-compare]');
    if (priceEl) priceEl.textContent = formatMoney(Number(resource.price) || 0, fmt, cur);
    if (cmpEl) {
      const cap = resource.compare_at_price;
      cmpEl.textContent =
        cap != null && Number(cap) > Number(resource.price) ? formatMoney(Number(cap), fmt, cur) : '';
    }

    const img = card.querySelector('[data-upsell-line-image]');
    if (img instanceof HTMLImageElement) {
      const mediaSrc = resource.featured_media?.preview_image?.src;
      const fallbackSrc = card.dataset.defaultImageSrc || '';
      if (mediaSrc) {
        img.src = mediaSrc.includes('?') ? `${mediaSrc}&width=200` : `${mediaSrc}?width=200`;
      } else if (fallbackSrc) {
        img.src = fallbackSrc;
      }
    }
  }

  /** @param {string} s */
  #escape(s) {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  #syncAvailability() {
    let catalog = null;
    const catTxt = this.querySelector('script[type="application/json"][data-upsell-variant-catalog]')?.textContent;
    if (catTxt) {
      try {
        catalog = JSON.parse(catTxt);
      } catch {
        catalog = null;
      }
    }

    let allOk = true;
    this.querySelectorAll('[data-upsell-card]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const cb = node.querySelector('[data-upsell-checkbox]');
      if (!(cb instanceof HTMLInputElement) || !cb.checked) return;
      const pid = String(node.dataset.productId ?? '');
      // Do not overwrite #byProduct from <script> JSON — morph can leave that tag stale while variant:update is correct.
      const row = this.#byProduct.get(pid);
      if (!row?.variantId) {
        allOk = false;
        return;
      }
      const v = catalog?.[String(pid)]?.find((x) => String(x.id) === String(row.variantId));
      if (v && !v.available) allOk = false;
    });

    this.dataset.allUpsellVariantsAvailable = allOk ? 'true' : 'false';
    const atc = /** @type {{ enable?: () => void; disable?: () => void } | null} */ (
      this.closest('product-form-component')?.querySelector('add-to-cart-component')
    );
    if (allOk) atc?.enable?.();
    else atc?.disable?.();
  }
}

if (!customElements.get('product-upsell-buy-buttons')) {
  customElements.define('product-upsell-buy-buttons', ProductUpsellBuyButtons);
}
